import {
  ChargeFlat,
  ChargePercent,
  Fee,
  FeeLine,
} from 'QuoteApp/services/endpoints/FeeStrategy';
import { PaymentScheduleType } from 'QuoteApp/services/endpoints/GetPaymentDetails';

import { toId } from './helpers';

export type NormalizedFee =
  | {
      id: string;
      label: string;
      subtype: 'PER_INSTALLMENT';
      kind: 'PERCENT' | 'FLAT';
      percent?: number;
      amount?: number;
    }
  | {
      id: string;
      label: string;
      subtype: 'PER_INCIDENT';
      kind: 'PERCENT' | 'FLAT';
      percent?: number;
      amount?: number;
      incidentType: string;
      daysAfterToCharge?: number;
    }
  | {
      id: string;
      label: string;
      subtype: 'PER_PRODUCT';
      kind: 'PERCENT' | 'FLAT';
      percent?: number;
      amount?: number;
    };

export function normalizeFees(fees: Fee[]): NormalizedFee[] {
  return fees.map<NormalizedFee>((fee) => {
    const id = toId(fee.name);
    const label = fee.displayName;

    if (fee.subtype === 'PER_INSTALLMENT') {
      return fee.charge.type === 'PERCENT'
        ? {
            id,
            label,
            subtype: 'PER_INSTALLMENT',
            kind: 'PERCENT',
            percent: fee.charge.percent,
          }
        : {
            id,
            label,
            subtype: 'PER_INSTALLMENT',
            kind: 'FLAT',
            amount: (fee.charge as ChargeFlat).amount,
          };
    }

    if (fee.subtype === 'PER_INCIDENT') {
      return fee.charge.type === 'PERCENT'
        ? {
            id,
            label,
            subtype: 'PER_INCIDENT',
            kind: 'PERCENT',
            percent: (fee.charge as ChargePercent).percent,
            incidentType: fee.incidentType,
            daysAfterToCharge: fee.daysAfterToCharge,
          }
        : {
            id,
            label,
            subtype: 'PER_INCIDENT',
            kind: 'FLAT',
            amount: (fee.charge as ChargeFlat).amount,
            incidentType: fee.incidentType,
            daysAfterToCharge: fee.daysAfterToCharge,
          };
    }

    return fee.charge.type === 'PERCENT'
      ? {
          id,
          label,
          subtype: 'PER_PRODUCT',
          kind: 'PERCENT',
          percent: (fee.charge as ChargePercent).percent,
        }
      : {
          id,
          label,
          subtype: 'PER_PRODUCT',
          kind: 'FLAT',
          amount: (fee.charge as ChargeFlat).amount,
        };
  });
}

type Formatter = (f: NormalizedFee) => FeeLine;

/**
 * Takes normalizedFees and ONLY applies the formatters that are present.
 * - If there are formatters for non-existent IDs: they are ignored.
 * - If a fee doesn’t have a formatter: it’s not included (strict/explicit).
 */
export function buildFeeLines(
  normalizedFees: NormalizedFee[],
  perFeeFormatters: Record<string, Formatter>
): FeeLine[] {
  return normalizedFees
    .map((nf) => {
      const fmt = perFeeFormatters[nf.id];
      return fmt ? fmt(nf) : null;
    })
    .filter((x): x is FeeLine => Boolean(x));
}

/*
 * Appends extras to lines, ignoring duplicates (by label).
 * Used to add static lines
 */
export function appendFeeLines(lines: FeeLine[], extras: FeeLine[]): FeeLine[] {
  const existing = new Set(lines.map((l) => l.label));
  const additions = extras.filter((e) => !existing.has(e.label));

  return [...lines, ...additions];
}

export function orderNormalizedFees(
  normalized: NormalizedFee[],
  orderById: string[]
): NormalizedFee[] {
  const rank = new Map<string, number>();

  orderById.forEach((id, i) => rank.set(id, i));

  const indexed = normalized.map((item, idx) => ({ item, idx }));

  indexed.sort((a, b) => {
    const ra = rank.has(a.item.id)
      ? (rank.get(a.item.id) as number)
      : Number.POSITIVE_INFINITY;

    const rb = rank.has(b.item.id)
      ? (rank.get(b.item.id) as number)
      : Number.POSITIVE_INFINITY;

    if (ra !== rb) return ra - rb;

    return a.idx - b.idx;
  });

  return indexed.map((x) => x.item);
}

export const getPaymentItemEntry = (
  paymentItems: PaymentScheduleType['items'],
  entryName: string
) => {
  return paymentItems.find((item) => item.name === entryName);
};

export const getPaymentScheduleEntry = (
  paymentSchedule: PaymentScheduleType[],
  entryType: string
) => {
  return paymentSchedule.find((item) => item.type === entryType);
};

export const getPaymentScheduleItemsByType = (
  paymentScheduleItems: PaymentScheduleType['items'],
  entryType: string
) => {
  return paymentScheduleItems.filter((item) => item.type === entryType);
};

export function onlyInLeftById(left: NormalizedFee[], right: NormalizedFee[]) {
  const rightIds = new Set(right.map((fee) => fee.id));

  return left.filter((fee) => !rightIds.has(fee.id));
}

export const isFLInstallmentFee = (name: string) =>
  name.trim().toLowerCase() === 'installment';
